import { deepCopy, deepSet, deepUnset, isDirty } from "@/utils";
import { toTitleCase } from "@/utils/str";
import mitt from "mitt";
import { inject, nextTick, onBeforeMount, onBeforeUnmount, onMounted, provide, ref } from "vue";

export const fieldEmits = (merge = []) => {
    return ['update:modelValue', 'update:state', 'hydrated:state', ...merge];
}

export const fieldProps = (merge = {}) => {
    const props = {
        id: {
            type: String,
            required: false,
        },
        label: {
            type: String,
            required: false,
        },
        hideLabel: {
            type: Boolean,
            default: false
        },
        name: {
            type: String,
            required: true,
        },
        placeholder: {
            type: String,
            required: false,
        },
        default: {
            required: false,
            default: '',
        },
        required: {
            type: Boolean,
            default: false,
        },
        markAsRequired: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        helpText: {
            type: String,
            required: false,
        },
        beforeDehydration: {
            type: Function,
            required: false,
        },
        dehydrateStateUsing: {
            type: Function,
            required: false,
        },
        min: {
            type: [String, Number],
        },
        max: {
            type: [String, Number],
        },
        size: {
            type: [String, Number],
        },
    };

    const model = {
        modelValue: {
            required: false,
        }
    };

    return { ...props, ...merge, ...model };
}

export const useField = ({
        props, emit, set, castStateUsing,
        beforeStateDehydrated, dehydrateStateUsing, validate,
        component = () => {},
    }) => {
    const form = inject('form');
    const state = ref();
    const initialState = ref();
    const errors = ref([]);
    let hydrated = false;
    let registration;

    const rules = {
        required: ({ state, fail }) => {
            if (!props.required) {
                return;
            }

            if (Array.isArray(state) && state.length === 0) {
                fail('Required');
            } else if (state !== false && !state) {
                fail('Required');
            }
        },
        max: ({ state, fail }) => {
            if (!props.max) {
                return;
            }

            if (Array.isArray(state) && state.length > props.max) {
                fail(`Max of ${props.max} allowed`);
            }
        }
    }

    const events = mitt();

    const setElementValue = (value) => {
        if (set) {
            set(value);
        }
    }

    const stateUpdated = (value, oldValue, trigger) => {
        emit('update:modelValue', value);

        const event = { value, oldValue, trigger };
        emit('update:state', event);
        events.emit('update:state', event);
    }

    const hydrate = (value) => {
        hydrated = true;
        let val = castState(value);

        initialState.value = deepCopy(val);
        state.value = deepCopy(val);

        emit('update:modelValue', deepCopy(val));
        emit('hydrated:state', {
            field,
            value: deepCopy(val),
        });
        events.emit('hydrated:state', {
            value: deepCopy(val),
        });
    }

    const castState = (value) => {
        if (castStateUsing) {
            return castStateUsing(deepCopy(value));
        }

        return value;
    }

    const field = {
        get events() {
            return events;
        },
        get props() {
            return props;
        },
        get form() {
            return form;
        },
        get state() {
            return deepCopy(state.value);
        },
        get initialState() {
            return deepCopy(initialState.value);
        },
        getComponent() {
            return component();
        },
        updateState({ value, statePath, event }) {
            const oldValue = deepCopy(state.value);

            value = deepCopy(value);

            if (statePath) {
                deepSet(state.value, statePath, value);
            } else {
                state.value = castState(value);
            }

            stateUpdated(state.value, oldValue, event);
        },
        set(value) {
            const val = deepCopy(value);
            setElementValue(val);
            this.updateState({ value: val });
        },
        reset() {
            const value = deepCopy(initialState.value);
            setElementValue(value);
            this.updateState({ value });
        },
        clear() {
            setElementValue(null);
            this.updateState({ value: null });
        },
        get id() {
            return props.id ? props.id : props.name;
        },
        get name() {
            return props.name;
        },
        get label() {
            return props.label ? props.label : toTitleCase(props.name);
        },
        get hideLabel() {
            return props.hideLabel;
        },
        get placeholder() {
            return props.placeholder;
        },
        get default() {
            return props.default;
        },
        get required() {
            return props.required;
        },
        get disabled() {
            return form.disabled || props.disabled
        },
        get markAsRequired() {
            return form.markAsRequired || props.markAsRequired;
        },
        get helpText() {
            return props.helpText;
        },
        get errors() {
            return errors.value;
        },
        setErrors(value) {
            errors.value = value ?? [];
        },
        pushError(...error) {
            errors.value.push(...error);
        },
        clearErrors() {
            this.setErrors([]);
        },
        get isDirty() {
            return isDirty(deepCopy(initialState.value), deepCopy(state.value));
        },
        async validate() {
            this.clearErrors();

            await nextTick();

            const state = deepCopy(field.state);
            let hasError = false;

            const fail = (err) => {
                this.pushError(err);
                hasError = true;
            };

            rules.required({ state, fail});

            if (!hasError) {
                rules.max({ state, fail });
            }

            if (validate) {
                validate({
                    state,
                    fail,
                });
            }
        },
        async hydrate(value) {
            hydrate(value);
        },
        async beforeDehydration() {
            const callbacks = [beforeStateDehydrated, props.beforeStateDehydrated].filter(Boolean);

            for (const callback of callbacks) {
                const res = await callback({ field });

                if (res === false) {
                    return false;
                }
            }
        },
        async dehydrate() {
            let dehydrated = deepCopy(field.state);

            if (dehydrated !== false && !dehydrated) {
                return dehydrated;
            }

            if (dehydrateStateUsing) {
                dehydrated = await dehydrateStateUsing({ field, dehydrated });
            }

            if (props.dehydrateStateUsing) {
                dehydrated = await props.dehydrateStateUsing({ field, dehydrated });
            }

            return dehydrated;
        },
        getComponent() {
            return component();
        },
    };

    onBeforeMount(() => {
        registration = form.register(field).then(() => {
            if (!hydrated) {
                hydrate(field.default);
            }
        });
    });

    onMounted(() => {
        registration.then(() => {
            setElementValue(state.value);
        });
    });

    onBeforeUnmount(() => {
        form.unregister(field.id);
    });

    provide('field', field);

    return field;
}
